import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';

import UnifiedUserExperienceHandler from 'bundles/unified-common/providers/UnifiedUserExperienceHandler';
import EnterpriseOrgUserExperienceDataProvider from 'bundles/unified-home-common/providers/org-scoped/data/EnterpriseOrgUserExperienceDataProvider';
import OrgScopedImpersonationDataProvider from 'bundles/unified-home-common/providers/org-scoped/data/OrgScopedImpersonationDataProvider';

/**
 * Org-scoped user experience handler: admin preview state (OrgScopedImpersonationDataProvider),
 * redirect + hold-render (UnifiedUserExperienceHandler) plus org user experience data
 * (EnterpriseOrgUserExperienceDataProvider).
 * Must be rendered inside a provider that supplies ThirdPartyOrganizationContext
 * (e.g. EnterpriseOrgHomePageProvider).
 */
const EnterpriseOrgUserExperienceHandler = ({ children }: React.PropsWithChildren<{}>) => {
  return (
    <StatsigProvider>
      {/*
        Rendered above UnifiedUserExperienceHandler so the redirect logic can read the admin's
        preview session. The handler holds back its children until the redirect resolves, so it
        can't read context from a provider below it.
      */}
      <OrgScopedImpersonationDataProvider>
        {/*
          Reaching an org-scoped XDP already means EnterprisePageGuard's own rollout-gate-or-override-org
          check passed, so UnifiedUserExperienceHandler doesn't need to re-check the raw Statsig gate here.
          Without this, override orgs (whose gate value can legitimately differ from what
          EnterprisePageGuard saw moments earlier) never get their membership data fetched.
        */}
        <UnifiedUserExperienceHandler assumeOrgScopedEligible>
          <EnterpriseOrgUserExperienceDataProvider>{children}</EnterpriseOrgUserExperienceDataProvider>
        </UnifiedUserExperienceHandler>
      </OrgScopedImpersonationDataProvider>
    </StatsigProvider>
  );
};

export default EnterpriseOrgUserExperienceHandler;
